import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Globe, 
  TrendingUp,
  Clock,
  Star,
  MessageSquare,
  ShoppingCart,
  CheckCircle,
  PoundSterling,
  Activity,
  BarChart3,
  GripVertical,
  CreditCard,
  Repeat,
  CalendarIcon,
  Mail,
  MousePointerClick,
  Eye,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Card Component
const SortableStatCard = ({ id, stat }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const Icon = stat.icon;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className={`bg-card/50 border-white/5 h-full ${isDragging ? 'ring-2 ring-primary' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div 
              {...listeners} 
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/10 transition-colors"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          <div className="text-2xl font-bold">{stat.value}</div>
          <div className="text-xs text-muted-foreground mt-1">{stat.title}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const DashboardStats = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardOrder, setCardOrder] = useState(() => {
    const saved = localStorage.getItem('dashboardCardOrder');
    return saved ? JSON.parse(saved) : null;
  });
  const [dateRange, setDateRange] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle date range selection
  const handleDateRangeChange = (value) => {
    setDateRange(value);
    const now = new Date();
    
    switch (value) {
      case 'this_month':
        setDateFrom(startOfMonth(now));
        setDateTo(endOfMonth(now));
        setShowCustomPicker(false);
        break;
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        setDateFrom(startOfMonth(lastMonth));
        setDateTo(endOfMonth(lastMonth));
        setShowCustomPicker(false);
        break;
      case 'this_year':
        setDateFrom(startOfYear(now));
        setDateTo(now);
        setShowCustomPicker(false);
        break;
      case 'custom':
        setShowCustomPicker(true);
        break;
      case 'all':
      default:
        setDateFrom(null);
        setDateTo(null);
        setShowCustomPicker(false);
        break;
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateFrom, dateTo]);

  const fetchStats = async () => {
    try {
      let url = `${API}/stats/dashboard`;
      const params = new URLSearchParams();
      if (dateFrom) {
        params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      }
      if (dateTo) {
        params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await axios.get(url, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  // Define all stat cards
  const allStatCards = [
    { 
      id: 'live-websites',
      title: 'Live Websites', 
      value: stats?.websites?.live || 0, 
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    { 
      id: 'previews-sent',
      title: 'Previews Sent', 
      value: stats?.websites?.preview_sent || 0, 
      icon: Activity,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    { 
      id: 'monthly-subscription',
      title: 'Monthly Subscription Income', 
      value: `£${(stats?.revenue?.monthly_subscription || 0).toLocaleString()}`, 
      icon: Repeat,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    },
    { 
      id: 'one-time-income',
      title: 'One-Time Income This Month', 
      value: `£${(stats?.revenue?.one_time_this_month || 0).toLocaleString()}`, 
      icon: CreditCard,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    { 
      id: 'pending-orders',
      title: 'Pending Orders', 
      value: stats?.orders?.pending || 0, 
      icon: ShoppingCart,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10'
    },
    { 
      id: 'paid-orders',
      title: 'Paid Orders', 
      value: stats?.orders?.paid || 0, 
      icon: CheckCircle,
      color: 'text-teal-500',
      bgColor: 'bg-teal-500/10'
    },
    { 
      id: 'favourites',
      title: 'Favourites', 
      value: stats?.favourites || 0, 
      icon: Star,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10'
    },
    { 
      id: 'contact-enquiries',
      title: 'Contact Enquiries', 
      value: stats?.contacts || 0, 
      icon: MessageSquare,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10'
    },
  ];

  // Get ordered cards based on saved order or default
  const defaultOrder = allStatCards.map(card => card.id);
  const currentOrder = cardOrder || defaultOrder;
  
  // Filter and order cards
  const orderedCards = currentOrder
    .map(id => allStatCards.find(card => card.id === id))
    .filter(Boolean);
  
  // Add any new cards that might not be in the saved order
  const newCards = allStatCards.filter(card => !currentOrder.includes(card.id));
  const finalCards = [...orderedCards, ...newCards];

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = finalCards.findIndex(card => card.id === active.id);
      const newIndex = finalCards.findIndex(card => card.id === over.id);
      
      const newOrder = arrayMove(finalCards, oldIndex, newIndex).map(card => card.id);
      setCardOrder(newOrder);
      localStorage.setItem('dashboardCardOrder', JSON.stringify(newOrder));
    }
  };

  // Calculate max value for chart
  const maxRevenue = Math.max(...(stats?.revenue?.monthly?.map(m => m.revenue) || [1]), 1);

  // Get display label for current date range
  const getDateRangeLabel = () => {
    if (dateRange === 'all') return 'All Time';
    if (dateRange === 'this_month') return 'This Month';
    if (dateRange === 'last_month') return 'Last Month';
    if (dateRange === 'this_year') return 'This Year';
    if (dateRange === 'custom' && dateFrom && dateTo) {
      return `${format(dateFrom, 'dd MMM')} - ${format(dateTo, 'dd MMM yyyy')}`;
    }
    return 'Select Period';
  };

  return (
    <div className="space-y-6" data-testid="dashboard-stats">
      {/* Date Range Selector */}
      <div className="flex justify-end gap-2 items-center">
        <Select value={dateRange} onValueChange={handleDateRangeChange}>
          <SelectTrigger className="w-48">
            <CalendarIcon className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Select period">{getDateRangeLabel()}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="this_year">This Year</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
        
        {showCustomPicker && (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  {dateFrom ? format(dateFrom, 'dd MMM yyyy') : 'From'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <span className="text-muted-foreground">to</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  {dateTo ? format(dateTo, 'dd MMM yyyy') : 'To'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>

      {/* Main Stats Grid - Draggable */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={finalCards.map(card => card.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {finalCards.map((stat) => (
              <SortableStatCard key={stat.id} id={stat.id} stat={stat} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Revenue Chart */}
      <Card className="bg-card/50 border-white/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Monthly Revenue
          </CardTitle>
          <CardDescription>Revenue trend over the last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 h-48">
            {stats?.revenue?.monthly?.map((month, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full bg-white/5 rounded-t-lg relative flex-1 flex items-end">
                  <div 
                    className="w-full bg-gradient-to-t from-primary to-primary/60 rounded-t-lg transition-all duration-500"
                    style={{ height: `${(month.revenue / maxRevenue) * 100}%`, minHeight: month.revenue > 0 ? '8px' : '0' }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{month.month}</span>
                <span className="text-xs font-medium">
                  £{month.revenue.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Summary */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Email Tracking Stats - NEW */}
        <Card className="bg-card/50 border-white/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-500" />
              Email Outreach
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{stats?.email_tracking?.sent || 0}</div>
                <div className="text-xs text-muted-foreground">Sent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{stats?.email_tracking?.opened || 0}</div>
                <div className="text-xs text-muted-foreground">Opened</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{stats?.email_tracking?.clicked || 0}</div>
                <div className="text-xs text-muted-foreground">Clicked</div>
              </div>
            </div>
            <div className="space-y-3 pt-3 border-t border-white/10">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" />
                  Open Rate
                </span>
                <span className="font-medium">
                  {stats?.email_tracking?.sent > 0 
                    ? `${Math.round((stats?.email_tracking?.opened / stats?.email_tracking?.sent) * 100)}%`
                    : '0%'}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <MousePointerClick className="w-3.5 h-3.5" />
                  Click Rate
                </span>
                <span className="font-medium">
                  {stats?.email_tracking?.sent > 0 
                    ? `${Math.round((stats?.email_tracking?.clicked / stats?.email_tracking?.sent) * 100)}%`
                    : '0%'}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" />
                  Follow-ups Sent
                </span>
                <span className="font-medium">{stats?.email_tracking?.follow_ups || 0}</span>
              </div>
              {(stats?.email_tracking?.bounced > 0 || stats?.email_tracking?.unsubscribed > 0) && (
                <div className="flex justify-between items-center text-sm pt-2 border-t border-white/5">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />
                    Bounced / Unsubscribed
                  </span>
                  <span className="font-medium text-orange-400">
                    {stats?.email_tracking?.bounced || 0} / {stats?.email_tracking?.unsubscribed || 0}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-white/5">
          <CardHeader>
            <CardTitle className="text-lg">Website Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Preview Sent</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full" 
                      style={{ width: `${((stats?.websites?.preview_sent || 0) / Math.max((stats?.websites?.preview_sent || 0) + (stats?.websites?.live || 0), 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8">{stats?.websites?.preview_sent || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Live</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full" 
                      style={{ width: `${((stats?.websites?.live || 0) / Math.max((stats?.websites?.preview_sent || 0) + (stats?.websites?.live || 0), 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8">{stats?.websites?.live || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-white/5">
          <CardHeader>
            <CardTitle className="text-lg">Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Pending</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-orange-500 rounded-full" 
                      style={{ width: `${((stats?.orders?.pending || 0) / Math.max(stats?.orders?.total || 1, 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8">{stats?.orders?.pending || 0}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Paid</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full" 
                      style={{ width: `${((stats?.orders?.paid || 0) / Math.max(stats?.orders?.total || 1, 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8">{stats?.orders?.paid || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardStats;
